import React, { useState, useEffect } from "react"
import { lumi } from "../lib/lumi"
import toast from "react-hot-toast"
import {CheckCircle, XCircle, Clock, Eye, X, Calendar, User, FileText, AlertTriangle} from 'lucide-react'

export default function AdminDocumentQueue() {
  const [pendingDocs, setPendingDocs] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedDoc, setSelectedDoc] = useState<any>(null)
  const [reviewNote, setReviewNote] = useState("")
  const [rejectionReason, setRejectionReason] = useState("")
  const [processing, setProcessing] = useState(false)

  useEffect(() => {
    fetchPendingDocuments()
  }, [])

  const fetchPendingDocuments = async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.Documents.list({
        filter: { status: "pending" },
        sort: { uploadedAt: -1 }
      })
      
      // Fetch user profiles for each document
      const docsWithUsers = await Promise.all(
        list.map(async (doc) => {
          try {
            const profiles = await lumi.entities.UserProfiles.list({
              filter: { userId: doc.userId }
            })
            return { ...doc, userProfile: profiles.list[0] || null }
          } catch {
            return { ...doc, userProfile: null }
          }
        })
      )
      
      setPendingDocs(docsWithUsers)
    } catch (error) {
      console.error(error)
      toast.error("Failed to load pending documents")
    } finally {
      setLoading(false)
    }
  }

  const handleApprove = async (doc: any) => {
    setProcessing(true)
    try {
      const admin = lumi.auth.user
      await lumi.entities.Documents.update(doc._id, {
        status: "verified",
        reviewedAt: new Date().toISOString(),
        reviewedBy: admin?.email || "admin",
        reviewerNote: reviewNote || "Document verified"
      })

      // Send approval email
      if (doc.userProfile?.email) {
        await lumi.tools.email.send({
          to: doc.userProfile.email,
          subject: `Document Approved: ${doc.docName}`,
          fromName: "Nursing Hive",
          html: `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
              <h2 style="color: #10b981;">✓ Document Approved</h2>
              <p>Dear ${doc.userProfile.fullName || "Nurse"},</p>
              <p>Your document <strong>${doc.docName}</strong> has been verified and approved.</p>
              <p style="background: #f0fdf4; padding: 15px; border-left: 4px solid #10b981; margin: 20px 0;">
                <strong>Status:</strong> Verified<br>
                <strong>Reviewed by:</strong> ${admin?.email || "Admin"}<br>
                <strong>Date:</strong> ${new Date().toLocaleDateString()}
              </p>
              ${reviewNote ? `<p><strong>Admin Note:</strong> ${reviewNote}</p>` : ""}
              <p>You can view your documents in your dashboard.</p>
              <p>Best regards,<br>Nursing Hive Team</p>
            </div>
          `
        })
      }

      toast.success("Document approved and email sent")
      setSelectedDoc(null)
      setReviewNote("")
      fetchPendingDocuments()
    } catch (error) {
      console.error(error)
      toast.error("Failed to approve document")
    } finally {
      setProcessing(false)
    }
  }

  const handleReject = async (doc: any) => {
    if (!rejectionReason.trim()) {
      toast.error("Please provide a rejection reason")
      return
    }

    setProcessing(true)
    try {
      const admin = lumi.auth.user
      await lumi.entities.Documents.update(doc._id, {
        status: "rejected",
        reviewedAt: new Date().toISOString(),
        reviewedBy: admin?.email || "admin",
        rejectionReason,
        reviewerNote: reviewNote || rejectionReason
      })

      // Send rejection email with detailed feedback
      if (doc.userProfile?.email) {
        await lumi.tools.email.send({
          to: doc.userProfile.email,
          subject: `Document Requires Resubmission: ${doc.docName}`,
          fromName: "Nursing Hive",
          html: `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
              <h2 style="color: #ef4444;">⚠ Document Rejected</h2>
              <p>Dear ${doc.userProfile.fullName || "Nurse"},</p>
              <p>Your document <strong>${doc.docName}</strong> requires resubmission.</p>
              <div style="background: #fef2f2; padding: 15px; border-left: 4px solid #ef4444; margin: 20px 0;">
                <p><strong>Rejection Reason:</strong></p>
                <p style="margin: 10px 0;">${rejectionReason}</p>
                ${reviewNote && reviewNote !== rejectionReason ? `<p style="margin-top: 15px;"><strong>Additional Notes:</strong> ${reviewNote}</p>` : ""}
              </div>
              <p><strong>Next Steps:</strong></p>
              <ol style="line-height: 1.8;">
                <li>Review the rejection reason carefully</li>
                <li>Prepare a corrected version of the document</li>
                <li>Upload the new document in your dashboard</li>
                <li>Our team will review it within 2-3 business days</li>
              </ol>
              <p style="background: #f0f9ff; padding: 15px; border-left: 4px solid #3b82f6; margin: 20px 0;">
                <strong>💡 Tip:</strong> Ensure your document is clear, legible, and contains all required information. Common issues include blurry images, missing signatures, or expired dates.
              </p>
              <p>If you have questions, please reply to this email or contact our support team.</p>
              <p>Best regards,<br>Nursing Hive Team</p>
            </div>
          `
        })
      }

      // Create audit log
      await lumi.entities.AuditLogs.create({
        userId: doc.userId,
        action: "document_rejected",
        entityType: "Documents",
        entityId: doc._id,
        details: {
          docType: doc.docType,
          docName: doc.docName,
          rejectionReason,
          reviewerNote,
          reviewedBy: admin?.email
        },
        performedBy: admin?.userId || "system",
        timestamp: new Date().toISOString()
      })

      toast.success("Document rejected and email sent")
      setSelectedDoc(null)
      setReviewNote("")
      setRejectionReason("")
      fetchPendingDocuments()
    } catch (error) {
      console.error(error)
      toast.error("Failed to reject document")
    } finally {
      setProcessing(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading pending documents...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Document Verification Queue</h1>
              <p className="text-gray-600 mt-1">Review and approve pending nurse documents</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">{pendingDocs.length}</div>
                <div className="text-sm text-gray-600">Pending</div>
              </div>
            </div>
          </div>
        </div>

        {pendingDocs.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">All Caught Up!</h3>
            <p className="text-gray-600">No pending documents to review at the moment.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pendingDocs.map((doc) => (
              <div key={doc._id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                <div className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-900">{doc.docName}</h3>
                      <p className="text-sm text-gray-600">{doc.category}</p>
                    </div>
                    <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Pending
                    </span>
                  </div>

                  <div className="space-y-2 text-sm text-gray-600 mb-4">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      <span>{doc.userProfile?.fullName || "Unknown"}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>Uploaded: {new Date(doc.uploadedAt).toLocaleDateString()}</span>
                    </div>
                    {doc.expiryDate && (
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-orange-500" />
                        <span>Expires: {new Date(doc.expiryDate).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => setSelectedDoc(doc)}
                    className="w-full bg-emerald-600 text-white py-2 rounded-lg hover:bg-emerald-700 flex items-center justify-center gap-2"
                  >
                    <Eye className="w-4 h-4" />
                    Review Document
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Review Modal */}
        {selectedDoc && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{selectedDoc.docName}</h2>
                  <p className="text-sm text-gray-600">Nurse: {selectedDoc.userProfile?.fullName || "Unknown"}</p>
                </div>
                <button
                  onClick={() => {
                    setSelectedDoc(null)
                    setReviewNote("")
                    setRejectionReason("")
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="grid lg:grid-cols-2 gap-6 p-6">
                {/* Document Preview */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Document Preview</h3>
                  {selectedDoc.fileUrl?.endsWith(".pdf") ? (
                    <iframe src={selectedDoc.fileUrl} className="w-full h-[500px] border border-gray-200 rounded-lg" />
                  ) : (
                    <img src={selectedDoc.fileUrl} alt={selectedDoc.docName} className="w-full rounded-lg border border-gray-200" />
                  )}
                  <a
                    href={selectedDoc.fileUrl}
                    download
                    className="mt-3 block text-center text-emerald-600 hover:text-emerald-700 text-sm"
                  >
                    Download Original File
                  </a>
                </div>

                {/* Review Form */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Review Actions</h3>
                  
                  <div className="bg-gray-50 p-4 rounded-lg mb-4 space-y-2 text-sm">
                    <p><strong>Category:</strong> {selectedDoc.category}</p>
                    <p><strong>Uploaded:</strong> {new Date(selectedDoc.uploadedAt).toLocaleString()}</p>
                    <p><strong>File Name:</strong> {selectedDoc.fileName}</p>
                    {selectedDoc.expiryDate && (
                      <p><strong>Expiry Date:</strong> {new Date(selectedDoc.expiryDate).toLocaleDateString()}</p>
                    )}
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Admin Notes (optional)
                      </label>
                      <textarea
                        value={reviewNote}
                        onChange={(e) => setReviewNote(e.target.value)}
                        placeholder="Add any notes or comments..."
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Rejection Reason (required for rejection)
                      </label>
                      <textarea
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        placeholder="e.g., Document is blurry, missing signature, expired date..."
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      />
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={() => handleApprove(selectedDoc)}
                        disabled={processing}
                        className="flex-1 bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2 font-medium"
                      >
                        <CheckCircle className="w-5 h-5" />
                        {processing ? "Processing..." : "Approve"}
                      </button>
                      <button
                        onClick={() => handleReject(selectedDoc)}
                        disabled={processing || !rejectionReason.trim()}
                        className="flex-1 bg-red-600 text-white py-3 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2 font-medium"
                      >
                        <XCircle className="w-5 h-5" />
                        {processing ? "Processing..." : "Reject"}
                      </button>
                    </div>

                    <p className="text-xs text-gray-500 text-center">
                      An email notification will be sent to the nurse automatically
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
